import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Zap,
  Activity,
} from 'lucide-react';
import { apiService } from '../services/api';
import { Card, CardHeader, CardContent } from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import TierPageBanner from '../components/portfolio/TierPageBanner';
import TierChip from '../components/portfolio/TierChip';
import { useTierPortfolio } from '../hooks/useTierPortfolio';

interface OrchestrationAuditLog {
  _id: string;
  accountId: string;
  userId: string;
  inputState: {
    accountBalances: {
      totalValue: number;
      availableCapital: number;
      cashBalance: number;
      investedValue: number;
    };
    riskTiers: Array<{
      id: string;
      name: string;
      riskTier: number;
      assetType: string;
      thesisTimeframe: string;
      allocationPct: number;
      enabled: boolean;
    }>;
    marketTheses: Record<string, Array<any>>;
    marketStatus?: string;
    unreviewedThesesCount: number;
    utilizationTarget?: number;
    currentUtilization?: number;
  };
  flowType: 'thesis-driven' | 'legacy' | 'skipped';
  thesisSynthesisRequestId?: string;
  steps: Array<{
    stepName: string;
    timestamp: Date;
    durationMs: number;
    input?: any;
    output?: any;
    errors?: string[];
    warnings?: string[];
    aiInteraction?: {
      model?: string;
      prompt?: string;
      promptLength?: number;
      response?: string;
      responseLength?: number;
      parsedResponse?: any;
      retryAttempts?: number;
      processingTimeMs?: number;
    };
    metadata?: any;
  }>;
  result: {
    status: 'success' | 'partial_success' | 'failed' | 'skipped';
    skipReason?: string;
    positionsCreated: number;
    positionsUpdated: number;
    recommendationsGenerated: number;
    totalAllocatedCapital: number;
    positionIds?: string[];
    recommendationIds?: string[];
    positions: Array<any>;
    recommendations: Array<any>;
  };
  processingTimeMs: number;
  aiModelsUsed: string[];
  promptVersions: string[];
  errors: string[];
  warnings: string[];
  startedAt: Date;
  completedAt: Date;
  notes?: string;
}

const OrchestrationAudits: React.FC = () => {
  const { tierFilter } = useTierPortfolio();
  const [accounts, setAccounts] = useState<
    Array<{ id: string; nickname: string; accountNumber: string }>
  >([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [auditLogs, setAuditLogs] = useState<OrchestrationAuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [expandedAIFields, setExpandedAIFields] = useState<Set<string>>(
    new Set(),
  );
  const [loadedPositions, setLoadedPositions] = useState<Record<string, any[]>>(
    {},
  );
  const [loadedRecommendations, setLoadedRecommendations] = useState<
    Record<string, any[]>
  >({});
  const [loadingItems, setLoadingItems] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const [totalLogs, setTotalLogs] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filterFlowType, setFilterFlowType] = useState<
    'thesis-driven' | 'legacy' | 'skipped' | 'all'
  >('all');
  const [filterStatus, setFilterStatus] = useState<
    'success' | 'partial_success' | 'failed' | 'skipped' | 'all'
  >('all');
  const pageSize = 20;

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAuditLogs = useCallback(
    async (page: number = 0) => {
      if (!selectedAccount) return;

      setLoading(true);
      try {
        const result = await apiService.getOrchestrationAuditLogs(
          selectedAccount,
          {
            limit: pageSize,
            skip: page * pageSize,
            flowType: filterFlowType !== 'all' ? filterFlowType : undefined,
            status: filterStatus !== 'all' ? filterStatus : undefined,
          },
        );

        if (page === 0) {
          setAuditLogs(result.logs);
        } else {
          setAuditLogs((prev) => [...prev, ...result.logs]);
        }

        setTotalLogs(result.total);
        setHasMore(result.hasMore);
        setCurrentPage(page);
      } catch (error) {
        console.error('Error loading audit logs:', error);
      } finally {
        setLoading(false);
      }
    },
    [selectedAccount, filterFlowType, filterStatus, pageSize],
  );

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      loadAuditLogs(0);
    }
  }, [selectedAccount, filterFlowType, filterStatus, loadAuditLogs]);

  const loadAccounts = async () => {
    try {
      const accountsData = await apiService.getAccounts();
      setAccounts(accountsData);
      if (accountsData.length > 0) {
        setSelectedAccount(accountsData[0].id);
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadMore = () => {
    if (hasMore && !loading) {
      loadAuditLogs(currentPage + 1);
    }
  };

  const toggleLogExpansion = async (logId: string) => {
    const wasExpanded = expandedLogs.has(logId);

    setExpandedLogs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });

    // Load positions and recommendations when expanding (not when collapsing)
    if (!wasExpanded) {
      const log = auditLogs.find((l) => l._id === logId);
      if (log) {
        const positionIds = log.result.positionIds || [];
        const recommendationIds = log.result.recommendationIds || [];
        const needsLoading =
          (positionIds.length > 0 && !loadedPositions[logId]) ||
          (recommendationIds.length > 0 && !loadedRecommendations[logId]);

        if (needsLoading) {
          setLoadingItems((prev) => new Set(prev).add(logId));

          try {
            // Fetch positions and recommendations in parallel
            const positionsPromise =
              positionIds.length > 0 && !loadedPositions[logId]
                ? apiService.getPositionsByIds(positionIds)
                : Promise.resolve({
                    positions: loadedPositions[logId] || [],
                    count: 0,
                  });

            const recommendationsPromise =
              recommendationIds.length > 0 && !loadedRecommendations[logId]
                ? apiService.getRecommendationsByIds(recommendationIds)
                : Promise.resolve({
                    recommendations: loadedRecommendations[logId] || [],
                    count: 0,
                  });

            const [positionsResult, recommendationsResult] = await Promise.all([
              positionsPromise,
              recommendationsPromise,
            ]);

            setLoadedPositions((prev) => ({
              ...prev,
              [logId]: positionsResult.positions || [],
            }));
            setLoadedRecommendations((prev) => ({
              ...prev,
              [logId]: recommendationsResult.recommendations || [],
            }));
          } catch (error) {
            console.error('Error loading positions/recommendations:', error);
          } finally {
            setLoadingItems((prev) => {
              const newSet = new Set(prev);
              newSet.delete(logId);
              return newSet;
            });
          }
        }
      }
    }
  };

  const toggleStepExpansion = (stepKey: string) => {
    setExpandedSteps((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stepKey)) {
        newSet.delete(stepKey);
      } else {
        newSet.add(stepKey);
      }
      return newSet;
    });
  };

  const toggleAIFieldExpansion = (fieldKey: string) => {
    setExpandedAIFields((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fieldKey)) {
        newSet.delete(fieldKey);
      } else {
        newSet.add(fieldKey);
      }
      return newSet;
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'partial_success':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'skipped':
        return <Clock className="h-5 w-5 text-gray-500" />;
      default:
        return <Activity className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'partial_success':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      case 'failed':
        return 'text-red-700 bg-red-50 border-red-200';
      case 'skipped':
        return 'text-gray-700 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString();
  };

  return (
    <div className="space-y-6">
      <TierPageBanner
        title="Orchestration by tier"
        description="Audit payloads include per-tier risk slots and tierId counts. Use the tier rail to focus the operator view."
      />
      {tierFilter !== 'all' ? (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          Filtering audits context:
          <TierChip riskTier={tierFilter} />
        </div>
      ) : null}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          Orchestration Audit Logs
        </h1>
        <button
          onClick={() => loadAuditLogs(0)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account
              </label>
              <select
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.nickname || account.accountNumber}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Flow Type
              </label>
              <select
                value={filterFlowType}
                onChange={(e) =>
                  setFilterFlowType(
                    e.target.value as
                      | 'thesis-driven'
                      | 'legacy'
                      | 'skipped'
                      | 'all',
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All Types</option>
                <option value="thesis-driven">Thesis-Driven</option>
                <option value="legacy">Legacy</option>
                <option value="skipped">Skipped</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) =>
                  setFilterStatus(
                    e.target.value as
                      | 'success'
                      | 'partial_success'
                      | 'failed'
                      | 'skipped'
                      | 'all',
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All Statuses</option>
                <option value="success">Success</option>
                <option value="partial_success">Partial Success</option>
                <option value="failed">Failed</option>
                <option value="skipped">Skipped</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit Logs List */}
      {loading && auditLogs.length === 0 ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : auditLogs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            No audit logs found for this account.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {auditLogs.map((log) => {
            const isExpanded = expandedLogs.has(log._id);
            return (
              <Card key={log._id} className="overflow-hidden">
                <CardHeader className="p-0">
                  <div
                    className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleLogExpansion(log._id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        )}
                        <div className="flex items-center gap-3">
                          {getStatusIcon(log.result.status)}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">
                                {formatDate(log.startedAt)}
                              </span>
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded border ${getStatusColor(
                                  log.result.status,
                                )}`}
                              >
                                {log.result.status.toUpperCase()}
                              </span>
                              <span className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded">
                                {log.flowType}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500 mt-1">
                              {log.steps.length} steps •{' '}
                              {formatDuration(log.processingTimeMs)} •{' '}
                              {log.result.positionsCreated} positions •{' '}
                              {log.result.recommendationsGenerated}{' '}
                              recommendations
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {log.result.totalAllocatedCapital > 0
                          ? `$${log.result.totalAllocatedCapital.toLocaleString()} allocated`
                          : 'No capital allocated'}
                      </div>
                    </div>
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent className="pt-0 border-t border-gray-200">
                    <div className="space-y-6 mt-4">
                      {/* Summary */}
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">
                          Summary
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="bg-gray-50 p-3 rounded">
                            <div className="text-xs text-gray-500">
                              Processing Time
                            </div>
                            <div className="text-sm font-semibold text-gray-900">
                              {formatDuration(log.processingTimeMs)}
                            </div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <div className="text-xs text-gray-500">
                              Positions Created
                            </div>
                            <div className="text-sm font-semibold text-gray-900">
                              {log.result.positionsCreated}
                            </div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <div className="text-xs text-gray-500">
                              Recommendations
                            </div>
                            <div className="text-sm font-semibold text-gray-900">
                              {log.result.recommendationsGenerated}
                            </div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <div className="text-xs text-gray-500">
                              Capital Allocated
                            </div>
                            <div className="text-sm font-semibold text-gray-900">
                              $
                              {log.result.totalAllocatedCapital.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Created Positions & Recommendations */}
                      {((log.result.positionIds &&
                        log.result.positionIds.length > 0) ||
                        (log.result.recommendationIds &&
                          log.result.recommendationIds.length > 0)) && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 mb-3">
                            Created Items
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {log.result.positionIds &&
                              log.result.positionIds.length > 0 && (
                                <div>
                                  <div className="text-xs font-semibold text-gray-700 mb-2">
                                    Positions ({log.result.positionIds.length})
                                  </div>
                                  <div className="bg-gray-50 p-3 rounded space-y-2 max-h-96 overflow-y-auto">
                                    {loadingItems.has(log._id) ? (
                                      <div className="text-xs text-gray-500 text-center py-4">
                                        Loading positions...
                                      </div>
                                    ) : loadedPositions[log._id] ? (
                                      loadedPositions[log._id].length > 0 ? (
                                        loadedPositions[log._id].map(
                                          (position: any, idx: number) => (
                                            <div
                                              key={position._id || idx}
                                              className="bg-white p-3 rounded border border-gray-200"
                                            >
                                              <div className="flex items-center justify-between mb-2">
                                                <div className="font-semibold text-sm text-gray-900">
                                                  {position.symbol}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                  {position.status}
                                                </div>
                                              </div>
                                              <div className="grid grid-cols-2 gap-2 text-xs">
                                                <div>
                                                  <span className="text-gray-500">
                                                    Capital:
                                                  </span>{' '}
                                                  <span className="font-medium">
                                                    $
                                                    {position.allocatedCapital?.toLocaleString() ||
                                                      0}
                                                  </span>
                                                </div>
                                                <div>
                                                  <span className="text-gray-500">
                                                    Confidence:
                                                  </span>{' '}
                                                  <span className="font-medium">
                                                    {position.confidence ||
                                                      'N/A'}
                                                  </span>
                                                </div>
                                                {position.priority && (
                                                  <div>
                                                    <span className="text-gray-500">
                                                      Priority:
                                                    </span>{' '}
                                                    <span className="font-medium">
                                                      {position.priority}
                                                    </span>
                                                  </div>
                                                )}
                                                {position.rank && (
                                                  <div>
                                                    <span className="text-gray-500">
                                                      Rank:
                                                    </span>{' '}
                                                    <span className="font-medium">
                                                      {position.rank}/
                                                      {
                                                        position.totalRecommendations
                                                      }
                                                    </span>
                                                  </div>
                                                )}
                                              </div>
                                              {position.targetHolding && (
                                                <div className="mt-2 pt-2 border-t border-gray-200 text-xs">
                                                  <div className="text-gray-500">
                                                    Target:{' '}
                                                    {
                                                      position.targetHolding
                                                        .quantity
                                                    }{' '}
                                                    @ $
                                                    {position.targetHolding.expectedFillPrice?.toFixed(
                                                      2,
                                                    )}
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          ),
                                        )
                                      ) : (
                                        <div className="text-xs text-gray-500 text-center py-4">
                                          No positions found (may have been
                                          deleted)
                                        </div>
                                      )
                                    ) : null}
                                  </div>
                                </div>
                              )}
                            {log.result.recommendationIds &&
                              log.result.recommendationIds.length > 0 && (
                                <div>
                                  <div className="text-xs font-semibold text-gray-700 mb-2">
                                    Recommendations (
                                    {log.result.recommendationIds.length})
                                  </div>
                                  <div className="bg-gray-50 p-3 rounded space-y-2 max-h-96 overflow-y-auto">
                                    {loadingItems.has(log._id) ? (
                                      <div className="text-xs text-gray-500 text-center py-4">
                                        Loading recommendations...
                                      </div>
                                    ) : loadedRecommendations[log._id] ? (
                                      loadedRecommendations[log._id].length >
                                      0 ? (
                                        loadedRecommendations[log._id].map(
                                          (
                                            recommendation: any,
                                            idx: number,
                                          ) => (
                                            <div
                                              key={recommendation._id || idx}
                                              className="bg-white p-3 rounded border border-gray-200"
                                            >
                                              <div className="flex items-center justify-between mb-2">
                                                <div className="font-semibold text-sm text-gray-900">
                                                  {recommendation.symbol ||
                                                    recommendation
                                                      .positionAnalysis
                                                      ?.symbol ||
                                                    'N/A'}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                  {recommendation.status}
                                                </div>
                                              </div>
                                              <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                                                <div>
                                                  <span className="text-gray-500">
                                                    Score:
                                                  </span>{' '}
                                                  <span className="font-medium">
                                                    {recommendation.recommendationScore ||
                                                      0}
                                                  </span>
                                                </div>
                                                <div>
                                                  <span className="text-gray-500">
                                                    Priority:
                                                  </span>{' '}
                                                  <span className="font-medium">
                                                    {recommendation.priority ||
                                                      'N/A'}
                                                  </span>
                                                </div>
                                                {recommendation.rank && (
                                                  <div>
                                                    <span className="text-gray-500">
                                                      Rank:
                                                    </span>{' '}
                                                    <span className="font-medium">
                                                      {recommendation.rank}/
                                                      {
                                                        recommendation.totalRecommendations
                                                      }
                                                    </span>
                                                  </div>
                                                )}
                                              </div>
                                              <div className="text-xs text-gray-600 mt-2 pt-2 border-t border-gray-200">
                                                <div className="font-medium mb-1">
                                                  Scores:
                                                </div>
                                                <div className="grid grid-cols-2 gap-1">
                                                  <div>
                                                    Timing:{' '}
                                                    {recommendation.marketTimingScore ||
                                                      0}
                                                  </div>
                                                  <div>
                                                    Alignment:{' '}
                                                    {recommendation.objectiveAlignmentScore ||
                                                      0}
                                                  </div>
                                                  <div>
                                                    Technical:{' '}
                                                    {recommendation.technicalAnalysisScore ||
                                                      0}
                                                  </div>
                                                  <div>
                                                    Fundamental:{' '}
                                                    {recommendation.fundamentalAnalysisScore ||
                                                      0}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          ),
                                        )
                                      ) : (
                                        <div className="text-xs text-gray-500 text-center py-4">
                                          No recommendations found (may have
                                          been deleted)
                                        </div>
                                      )
                                    ) : null}
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>
                      )}

                      {/* Input State */}
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">
                          Input State
                        </h3>
                        <div className="bg-gray-50 p-4 rounded text-sm">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <div className="text-gray-500">Total Value</div>
                              <div className="font-semibold">
                                $
                                {log.inputState.accountBalances.totalValue.toLocaleString()}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-500">
                                Available Capital
                              </div>
                              <div className="font-semibold">
                                $
                                {log.inputState.accountBalances.availableCapital.toLocaleString()}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-500">Cash Balance</div>
                              <div className="font-semibold">
                                $
                                {log.inputState.accountBalances.cashBalance.toLocaleString()}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-500">
                                Unreviewed Theses
                              </div>
                              <div className="font-semibold">
                                {log.inputState.unreviewedThesesCount}
                              </div>
                            </div>
                          </div>
                          {log.inputState.riskTiers && (
                            <div className="mt-4">
                              <div className="text-gray-500 mb-2">
                                Risk Tiers ({log.inputState.riskTiers.length})
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {log.inputState.riskTiers.map((tier) => (
                                  <span
                                    key={tier.id}
                                    className="px-2 py-1 text-xs bg-white rounded border"
                                  >
                                    {tier.name} ({tier.allocationPct * 100}%)
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Steps */}
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">
                          Steps ({log.steps.length})
                        </h3>
                        <div className="space-y-2">
                          {log.steps.map((step, index) => {
                            const stepKey = `${log._id}-step-${index}`;
                            const isStepExpanded = expandedSteps.has(stepKey);
                            return (
                              <div
                                key={index}
                                className="border border-gray-200 rounded"
                              >
                                <div
                                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                                  onClick={() => toggleStepExpansion(stepKey)}
                                >
                                  <div className="flex items-center gap-3">
                                    {isStepExpanded ? (
                                      <ChevronDown className="h-4 w-4 text-gray-400" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-gray-400" />
                                    )}
                                    <div>
                                      <div className="font-medium text-sm text-gray-900">
                                        {step.stepName}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {formatDuration(step.durationMs)} •{' '}
                                        {formatDate(step.timestamp)}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {step.errors && step.errors.length > 0 && (
                                      <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                                        {step.errors.length} error(s)
                                      </span>
                                    )}
                                    {step.warnings &&
                                      step.warnings.length > 0 && (
                                        <span className="text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
                                          {step.warnings.length} warning(s)
                                        </span>
                                      )}
                                    {step.aiInteraction && (
                                      <Zap className="h-4 w-4 text-yellow-500" />
                                    )}
                                  </div>
                                </div>

                                {isStepExpanded && (
                                  <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-4">
                                    {/* AI Interaction */}
                                    {step.aiInteraction && (
                                      <div>
                                        <div className="flex items-center gap-2 mb-2">
                                          <Zap className="h-4 w-4 text-yellow-500" />
                                          <span className="text-sm font-semibold text-gray-900">
                                            AI Interaction
                                          </span>
                                        </div>
                                        <div className="bg-white p-3 rounded text-xs space-y-2">
                                          <div>
                                            <span className="text-gray-500">
                                              Model:
                                            </span>{' '}
                                            <span className="font-medium">
                                              {step.aiInteraction.model ||
                                                'N/A'}
                                            </span>
                                          </div>
                                          {step.aiInteraction.prompt && (
                                            <div>
                                              <div className="flex items-center justify-between mb-1">
                                                <div className="text-gray-500">
                                                  Prompt (
                                                  {step.aiInteraction
                                                    .promptLength ||
                                                    step.aiInteraction.prompt
                                                      ?.length ||
                                                    0}{' '}
                                                  chars):
                                                </div>
                                                {(step.aiInteraction
                                                  .promptLength ||
                                                  step.aiInteraction.prompt
                                                    ?.length ||
                                                  0) > 500 && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      toggleAIFieldExpansion(
                                                        `${stepKey}-prompt`,
                                                      );
                                                    }}
                                                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                                                  >
                                                    {expandedAIFields.has(
                                                      `${stepKey}-prompt`,
                                                    )
                                                      ? 'Collapse'
                                                      : 'Expand'}
                                                  </button>
                                                )}
                                              </div>
                                              <pre
                                                className={`bg-gray-50 p-2 rounded overflow-auto text-xs ${
                                                  expandedAIFields.has(
                                                    `${stepKey}-prompt`,
                                                  )
                                                    ? ''
                                                    : 'max-h-40'
                                                }`}
                                              >
                                                {(() => {
                                                  const prompt =
                                                    step.aiInteraction.prompt ||
                                                    '';
                                                  const promptLength =
                                                    step.aiInteraction
                                                      .promptLength ||
                                                    prompt.length;
                                                  const isExpanded =
                                                    expandedAIFields.has(
                                                      `${stepKey}-prompt`,
                                                    );
                                                  return isExpanded ||
                                                    promptLength <= 500
                                                    ? prompt
                                                    : `${prompt.substring(
                                                        0,
                                                        500,
                                                      )}...`;
                                                })()}
                                              </pre>
                                            </div>
                                          )}
                                          {step.aiInteraction.response && (
                                            <div>
                                              <div className="flex items-center justify-between mb-1">
                                                <div className="text-gray-500">
                                                  Response (
                                                  {step.aiInteraction
                                                    .responseLength ||
                                                    (typeof step.aiInteraction
                                                      .response === 'string'
                                                      ? step.aiInteraction
                                                          .response.length
                                                      : JSON.stringify(
                                                          step.aiInteraction
                                                            .response,
                                                        ).length) ||
                                                    0}{' '}
                                                  chars):
                                                </div>
                                                {(() => {
                                                  const responseLength =
                                                    step.aiInteraction
                                                      .responseLength ||
                                                    (typeof step.aiInteraction
                                                      .response === 'string'
                                                      ? step.aiInteraction
                                                          .response.length
                                                      : JSON.stringify(
                                                          step.aiInteraction
                                                            .response,
                                                        ).length) ||
                                                    0;
                                                  return responseLength > 500;
                                                })() && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      toggleAIFieldExpansion(
                                                        `${stepKey}-response`,
                                                      );
                                                    }}
                                                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                                                  >
                                                    {expandedAIFields.has(
                                                      `${stepKey}-response`,
                                                    )
                                                      ? 'Collapse'
                                                      : 'Expand'}
                                                  </button>
                                                )}
                                              </div>
                                              <pre
                                                className={`bg-gray-50 p-2 rounded overflow-auto text-xs ${
                                                  expandedAIFields.has(
                                                    `${stepKey}-response`,
                                                  )
                                                    ? ''
                                                    : 'max-h-40'
                                                }`}
                                              >
                                                {(() => {
                                                  const isExpanded =
                                                    expandedAIFields.has(
                                                      `${stepKey}-response`,
                                                    );
                                                  const responseStr =
                                                    typeof step.aiInteraction
                                                      .response === 'string'
                                                      ? step.aiInteraction
                                                          .response
                                                      : JSON.stringify(
                                                          step.aiInteraction
                                                            .response,
                                                          null,
                                                          2,
                                                        );
                                                  return isExpanded ||
                                                    responseStr.length <= 500
                                                    ? responseStr
                                                    : `${responseStr.substring(
                                                        0,
                                                        500,
                                                      )}...`;
                                                })()}
                                              </pre>
                                            </div>
                                          )}
                                          {step.aiInteraction
                                            .parsedResponse && (
                                            <div>
                                              <div className="flex items-center justify-between mb-1">
                                                <div className="text-gray-500">
                                                  Parsed Response:
                                                </div>
                                                {(() => {
                                                  const parsedStr =
                                                    JSON.stringify(
                                                      step.aiInteraction
                                                        .parsedResponse,
                                                      null,
                                                      2,
                                                    );
                                                  return parsedStr.length >
                                                    1000 ? (
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleAIFieldExpansion(
                                                          `${stepKey}-parsed`,
                                                        );
                                                      }}
                                                      className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                                                    >
                                                      {expandedAIFields.has(
                                                        `${stepKey}-parsed`,
                                                      )
                                                        ? 'Collapse'
                                                        : 'Expand'}
                                                    </button>
                                                  ) : null;
                                                })()}
                                              </div>
                                              <pre
                                                className={`bg-gray-50 p-2 rounded overflow-auto text-xs ${
                                                  expandedAIFields.has(
                                                    `${stepKey}-parsed`,
                                                  )
                                                    ? ''
                                                    : 'max-h-40'
                                                }`}
                                              >
                                                {(() => {
                                                  const parsedStr =
                                                    JSON.stringify(
                                                      step.aiInteraction
                                                        .parsedResponse,
                                                      null,
                                                      2,
                                                    );
                                                  const isExpanded =
                                                    expandedAIFields.has(
                                                      `${stepKey}-parsed`,
                                                    );
                                                  return isExpanded ||
                                                    parsedStr.length <= 1000
                                                    ? parsedStr
                                                    : `${parsedStr.substring(
                                                        0,
                                                        1000,
                                                      )}...`;
                                                })()}
                                              </pre>
                                            </div>
                                          )}
                                          {step.aiInteraction
                                            .processingTimeMs && (
                                            <div>
                                              <span className="text-gray-500">
                                                Processing Time:
                                              </span>{' '}
                                              <span className="font-medium">
                                                {formatDuration(
                                                  step.aiInteraction
                                                    .processingTimeMs,
                                                )}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Input/Output */}
                                    {/* Special handling for reviewAccountPositions step */}
                                    {step.stepName ===
                                      'reviewAccountPositions' &&
                                    step.output ? (
                                      <div className="space-y-4">
                                        <div>
                                          <div className="text-xs font-semibold text-gray-700 mb-2">
                                            Position Review Summary
                                          </div>
                                          <div className="bg-white p-3 rounded border border-gray-200">
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
                                              <div>
                                                <span className="text-gray-500">
                                                  Active Positions:
                                                </span>{' '}
                                                <span className="font-semibold">
                                                  {step.output
                                                    .activePositionsCount || 0}
                                                </span>
                                              </div>
                                              <div>
                                                <span className="text-gray-500">
                                                  Positions Reviewed:
                                                </span>{' '}
                                                <span className="font-semibold">
                                                  {step.output.positionsReviewed
                                                    ?.length || 0}
                                                </span>
                                              </div>
                                              <div>
                                                <span className="text-gray-500">
                                                  AI Interactions:
                                                </span>{' '}
                                                <span className="font-semibold">
                                                  {step.output.aiInteractions
                                                    ?.length || 0}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Tier Position Counts */}
                                        {step.output.tierPositionCounts &&
                                          step.output.tierPositionCounts
                                            .length > 0 && (
                                            <div>
                                              <div className="text-xs font-semibold text-gray-700 mb-2">
                                                Tier Position Summary
                                              </div>
                                              <div className="space-y-2">
                                                {step.output.tierPositionCounts.map(
                                                  (
                                                    tierCount: any,
                                                    idx: number,
                                                  ) => (
                                                    <div
                                                      key={idx}
                                                      className="bg-white p-3 rounded border border-gray-200"
                                                    >
                                                      <div className="flex items-center justify-between mb-2">
                                                        <div className="font-semibold text-sm text-gray-900">
                                                          {tierCount.tierName}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                          {tierCount.tierId}
                                                        </div>
                                                      </div>
                                                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                                                        <div>
                                                          <span className="text-gray-500">
                                                            Active:
                                                          </span>{' '}
                                                          <span className="font-semibold">
                                                            {
                                                              tierCount.activePositionsCount
                                                            }
                                                          </span>
                                                        </div>
                                                        <div>
                                                          <span className="text-gray-500">
                                                            Approved:
                                                          </span>{' '}
                                                          <span className="font-semibold">
                                                            {
                                                              tierCount.approvedPositionsCount
                                                            }
                                                          </span>
                                                        </div>
                                                        <div>
                                                          <span className="text-gray-500">
                                                            Total:
                                                          </span>{' '}
                                                          <span className="font-semibold">
                                                            {
                                                              tierCount.totalActiveOrApproved
                                                            }
                                                          </span>
                                                        </div>
                                                        <div>
                                                          <span className="text-gray-500">
                                                            Max:
                                                          </span>{' '}
                                                          <span className="font-semibold">
                                                            {
                                                              tierCount.maxPositions
                                                            }
                                                          </span>
                                                        </div>
                                                        <div>
                                                          <span className="text-gray-500">
                                                            Needed:
                                                          </span>{' '}
                                                          <span
                                                            className={`font-semibold ${
                                                              tierCount.positionsNeeded >
                                                              0
                                                                ? 'text-blue-600'
                                                                : 'text-gray-600'
                                                            }`}
                                                          >
                                                            {
                                                              tierCount.positionsNeeded
                                                            }
                                                          </span>
                                                        </div>
                                                      </div>
                                                      {tierCount.positions &&
                                                        tierCount.positions
                                                          .length > 0 && (
                                                          <div className="mt-2 pt-2 border-t border-gray-200">
                                                            <div className="text-xs text-gray-500 mb-1">
                                                              Positions:
                                                            </div>
                                                            <div className="flex flex-wrap gap-1">
                                                              {tierCount.positions.map(
                                                                (
                                                                  pos: any,
                                                                  posIdx: number,
                                                                ) => (
                                                                  <span
                                                                    key={posIdx}
                                                                    className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded"
                                                                  >
                                                                    {pos.symbol}{' '}
                                                                    (
                                                                    {pos.status}
                                                                    )
                                                                  </span>
                                                                ),
                                                              )}
                                                            </div>
                                                          </div>
                                                        )}
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                            </div>
                                          )}

                                        {/* Positions Reviewed Details */}
                                        {step.output.positionsReviewed &&
                                          step.output.positionsReviewed.length >
                                            0 && (
                                            <div>
                                              <div className="text-xs font-semibold text-gray-700 mb-2">
                                                Positions Reviewed (
                                                {
                                                  step.output.positionsReviewed
                                                    .length
                                                }
                                                )
                                              </div>
                                              <div className="space-y-2 max-h-96 overflow-y-auto">
                                                {step.output.positionsReviewed.map(
                                                  (
                                                    position: any,
                                                    idx: number,
                                                  ) => (
                                                    <div
                                                      key={idx}
                                                      className="bg-white p-3 rounded border border-gray-200"
                                                    >
                                                      <div className="flex items-center justify-between mb-2">
                                                        <div className="font-semibold text-sm text-gray-900">
                                                          {position.symbol}
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                          <span className="text-xs text-gray-500">
                                                            {position.status}
                                                          </span>
                                                          {position.reviewTriggered && (
                                                            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                                                              AI Reviewed
                                                            </span>
                                                          )}
                                                        </div>
                                                      </div>

                                                      {/* Health Check */}
                                                      {position.healthCheck && (
                                                        <div className="mb-2 pb-2 border-b border-gray-200">
                                                          <div className="text-xs font-medium text-gray-700 mb-1">
                                                            Health Check:
                                                          </div>
                                                          <div className="grid grid-cols-2 gap-2 text-xs">
                                                            <div>
                                                              <span className="text-gray-500">
                                                                Risk Level:
                                                              </span>{' '}
                                                              <span
                                                                className={`font-medium ${
                                                                  position
                                                                    .healthCheck
                                                                    .riskLevel ===
                                                                  'high'
                                                                    ? 'text-red-600'
                                                                    : position
                                                                          .healthCheck
                                                                          .riskLevel ===
                                                                        'medium'
                                                                      ? 'text-yellow-600'
                                                                      : 'text-green-600'
                                                                }`}
                                                              >
                                                                {position
                                                                  .healthCheck
                                                                  .riskLevel ||
                                                                  'unknown'}
                                                              </span>
                                                            </div>
                                                            {position
                                                              .healthCheck
                                                              .totalReturn !==
                                                              undefined && (
                                                              <div>
                                                                <span className="text-gray-500">
                                                                  Total Return:
                                                                </span>{' '}
                                                                <span
                                                                  className={`font-medium ${
                                                                    position
                                                                      .healthCheck
                                                                      .totalReturn >=
                                                                    0
                                                                      ? 'text-green-600'
                                                                      : 'text-red-600'
                                                                  }`}
                                                                >
                                                                  {position.healthCheck.totalReturn.toFixed(
                                                                    2,
                                                                  )}
                                                                  %
                                                                </span>
                                                              </div>
                                                            )}
                                                            {position
                                                              .healthCheck
                                                              .primaryConcern && (
                                                              <div className="col-span-2">
                                                                <span className="text-gray-500">
                                                                  Concern:
                                                                </span>{' '}
                                                                <span className="font-medium">
                                                                  {
                                                                    position
                                                                      .healthCheck
                                                                      .primaryConcern
                                                                  }
                                                                </span>
                                                              </div>
                                                            )}
                                                          </div>
                                                        </div>
                                                      )}

                                                      {/* Risk Assessment */}
                                                      {position.riskAssessment && (
                                                        <div className="mb-2 pb-2 border-b border-gray-200">
                                                          <div className="text-xs font-medium text-gray-700 mb-1">
                                                            Risk Assessment:
                                                          </div>
                                                          <div className="text-xs">
                                                            <span className="text-gray-500">
                                                              Risk Level:
                                                            </span>{' '}
                                                            <span
                                                              className={`font-medium ${
                                                                position
                                                                  .riskAssessment
                                                                  .riskLevel ===
                                                                'high'
                                                                  ? 'text-red-600'
                                                                  : position
                                                                        .riskAssessment
                                                                        .riskLevel ===
                                                                      'medium'
                                                                    ? 'text-yellow-600'
                                                                    : 'text-green-600'
                                                              }`}
                                                            >
                                                              {
                                                                position
                                                                  .riskAssessment
                                                                  .riskLevel
                                                              }
                                                            </span>
                                                            {position
                                                              .riskAssessment
                                                              .concerns &&
                                                              position
                                                                .riskAssessment
                                                                .concerns
                                                                .length > 0 && (
                                                                <div className="mt-1">
                                                                  <span className="text-gray-500">
                                                                    Concerns:
                                                                  </span>
                                                                  <ul className="list-disc list-inside ml-2">
                                                                    {position.riskAssessment.concerns.map(
                                                                      (
                                                                        concern: string,
                                                                        cIdx: number,
                                                                      ) => (
                                                                        <li
                                                                          key={
                                                                            cIdx
                                                                          }
                                                                          className="text-gray-600"
                                                                        >
                                                                          {
                                                                            concern
                                                                          }
                                                                        </li>
                                                                      ),
                                                                    )}
                                                                  </ul>
                                                                </div>
                                                              )}
                                                          </div>
                                                        </div>
                                                      )}

                                                      {/* AI Analysis */}
                                                      {position.aiAnalysis && (
                                                        <div>
                                                          <div className="text-xs font-medium text-gray-700 mb-1">
                                                            AI Analysis:
                                                          </div>
                                                          <div className="text-xs space-y-1">
                                                            <div>
                                                              <span className="text-gray-500">
                                                                Recommendation:
                                                              </span>{' '}
                                                              <span
                                                                className={`font-semibold ${
                                                                  position
                                                                    .aiAnalysis
                                                                    .recommendation ===
                                                                  'close'
                                                                    ? 'text-red-600'
                                                                    : position
                                                                          .aiAnalysis
                                                                          .recommendation ===
                                                                        'keep'
                                                                      ? 'text-green-600'
                                                                      : 'text-yellow-600'
                                                                }`}
                                                              >
                                                                {position.aiAnalysis.recommendation?.toUpperCase()}
                                                              </span>
                                                              {position
                                                                .aiAnalysis
                                                                .confidence && (
                                                                <span className="text-gray-500 ml-2">
                                                                  (
                                                                  {
                                                                    position
                                                                      .aiAnalysis
                                                                      .confidence
                                                                  }
                                                                  % confidence)
                                                                </span>
                                                              )}
                                                            </div>
                                                            {position.aiAnalysis
                                                              .reasoning &&
                                                              position
                                                                .aiAnalysis
                                                                .reasoning
                                                                .length > 0 && (
                                                                <div>
                                                                  <span className="text-gray-500">
                                                                    Reasoning:
                                                                  </span>
                                                                  <ul className="list-disc list-inside ml-2 mt-1">
                                                                    {position.aiAnalysis.reasoning.map(
                                                                      (
                                                                        reason: string,
                                                                        rIdx: number,
                                                                      ) => (
                                                                        <li
                                                                          key={
                                                                            rIdx
                                                                          }
                                                                          className="text-gray-600"
                                                                        >
                                                                          {
                                                                            reason
                                                                          }
                                                                        </li>
                                                                      ),
                                                                    )}
                                                                  </ul>
                                                                </div>
                                                              )}
                                                          </div>
                                                        </div>
                                                      )}

                                                      {/* Error */}
                                                      {position.error && (
                                                        <div className="mt-2 pt-2 border-t border-red-200">
                                                          <div className="text-xs text-red-600">
                                                            <span className="font-medium">
                                                              Error:
                                                            </span>{' '}
                                                            {position.error}
                                                          </div>
                                                        </div>
                                                      )}
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                            </div>
                                          )}

                                        {/* AI Interactions */}
                                        {step.output.aiInteractions &&
                                          step.output.aiInteractions.length >
                                            0 && (
                                            <div>
                                              <div className="text-xs font-semibold text-gray-700 mb-2">
                                                AI Interactions (
                                                {
                                                  step.output.aiInteractions
                                                    .length
                                                }
                                                )
                                              </div>
                                              <div className="space-y-2 max-h-96 overflow-y-auto">
                                                {step.output.aiInteractions.map(
                                                  (
                                                    interaction: any,
                                                    idx: number,
                                                  ) => (
                                                    <div
                                                      key={idx}
                                                      className="bg-white p-3 rounded border border-gray-200"
                                                    >
                                                      <div className="flex items-center justify-between mb-2">
                                                        <div className="font-semibold text-sm text-gray-900">
                                                          {interaction.symbol}
                                                        </div>
                                                        {interaction.model && (
                                                          <div className="text-xs text-gray-500">
                                                            {interaction.model}
                                                          </div>
                                                        )}
                                                      </div>
                                                      {interaction.prompt && (
                                                        <div className="mb-2">
                                                          <div className="text-xs font-medium text-gray-700 mb-1">
                                                            Prompt:
                                                          </div>
                                                          <pre className="bg-gray-50 p-2 rounded text-xs overflow-auto max-h-32">
                                                            {interaction.prompt
                                                              .length > 500
                                                              ? `${interaction.prompt.substring(0, 500)}...`
                                                              : interaction.prompt}
                                                          </pre>
                                                        </div>
                                                      )}
                                                      {interaction.response && (
                                                        <div>
                                                          <div className="text-xs font-medium text-gray-700 mb-1">
                                                            Response:
                                                          </div>
                                                          <pre className="bg-gray-50 p-2 rounded text-xs overflow-auto max-h-32">
                                                            {interaction
                                                              .response.length >
                                                            500
                                                              ? `${interaction.response.substring(0, 500)}...`
                                                              : interaction.response}
                                                          </pre>
                                                        </div>
                                                      )}
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                            </div>
                                          )}

                                        {/* Fallback to JSON if structure is unexpected */}
                                        {!step.output.positionsReviewed &&
                                          !step.output.aiInteractions && (
                                            <div>
                                              <div className="text-xs font-semibold text-gray-700 mb-1">
                                                Output
                                              </div>
                                              <pre className="bg-white p-2 rounded overflow-auto max-h-40 text-xs">
                                                {JSON.stringify(
                                                  step.output,
                                                  null,
                                                  2,
                                                )}
                                              </pre>
                                            </div>
                                          )}
                                      </div>
                                    ) : (
                                      /* Default display for other steps */
                                      (step.input || step.output) && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                          {step.input && (
                                            <div>
                                              <div className="text-xs font-semibold text-gray-700 mb-1">
                                                Input
                                              </div>
                                              <pre className="bg-white p-2 rounded overflow-auto max-h-40 text-xs">
                                                {JSON.stringify(
                                                  step.input,
                                                  null,
                                                  2,
                                                )}
                                              </pre>
                                            </div>
                                          )}
                                          {step.output && (
                                            <div>
                                              <div className="text-xs font-semibold text-gray-700 mb-1">
                                                Output
                                              </div>
                                              <pre className="bg-white p-2 rounded overflow-auto max-h-40 text-xs">
                                                {JSON.stringify(
                                                  step.output,
                                                  null,
                                                  2,
                                                )}
                                              </pre>
                                            </div>
                                          )}
                                        </div>
                                      )
                                    )}

                                    {/* Errors */}
                                    {step.errors && step.errors.length > 0 && (
                                      <div>
                                        <div className="text-xs font-semibold text-red-700 mb-1">
                                          Errors
                                        </div>
                                        <ul className="list-disc list-inside text-xs text-red-600 space-y-1">
                                          {step.errors.map((error, i) => (
                                            <li key={i}>{error}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {/* Warnings */}
                                    {step.warnings &&
                                      step.warnings.length > 0 && (
                                        <div>
                                          <div className="text-xs font-semibold text-yellow-700 mb-1">
                                            Warnings
                                          </div>
                                          <ul className="list-disc list-inside text-xs text-yellow-600 space-y-1">
                                            {step.warnings.map((warning, i) => (
                                              <li key={i}>{warning}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Errors & Warnings */}
                      {(log.errors.length > 0 || log.warnings.length > 0) && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 mb-3">
                            Global Errors & Warnings
                          </h3>
                          {log.errors.length > 0 && (
                            <div className="mb-3">
                              <div className="text-xs font-semibold text-red-700 mb-1">
                                Errors ({log.errors.length})
                              </div>
                              <ul className="list-disc list-inside text-xs text-red-600 space-y-1 bg-red-50 p-3 rounded">
                                {log.errors.map((error, i) => (
                                  <li key={i}>{error}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {log.warnings.length > 0 && (
                            <div>
                              <div className="text-xs font-semibold text-yellow-700 mb-1">
                                Warnings ({log.warnings.length})
                              </div>
                              <ul className="list-disc list-inside text-xs text-yellow-600 space-y-1 bg-yellow-50 p-3 rounded">
                                {log.warnings.map((warning, i) => (
                                  <li key={i}>{warning}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}

          {/* Load More */}
          {hasMore && (
            <div className="flex justify-center py-4">
              <button
                onClick={loadMore}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}

          {/* Pagination Info */}
          <div className="text-center text-sm text-gray-500 py-4">
            Showing {auditLogs.length} of {totalLogs} audit logs
          </div>
        </div>
      )}
    </div>
  );
};

export default OrchestrationAudits;
